import React from 'react';
import {
  Upload, Loader2, Plus, Search,
  ChevronDown, ChevronUp, Mic, Grid, Calendar,
  List, X, Brain, Sparkles, Mail, MessageSquare, PencilLine, Phone, CheckCircle2
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import GuestGuideForm from '../guest-guides/GuestGuideForm';
import GuestGuidesList from '../guest-guides/GuestGuidesList';
import { useGuidePartnersAdminState } from '../hooks/useGuidePartnersAdminState';
import { formatDate } from '../adminFormatters';
import {
  INVITE_STAGE_OPTIONS,
  STATUS_OPTIONS,
} from '../guest-guides/constants';

const SCHEDULING_CONTEXT_ROWS = [
  {
    key: 'story_summary',
    label: 'Briefly describe the story you may want to share',
    emptyLabel: 'No story summary submitted yet.',
  },
  {
    key: 'main_takeaway',
    label: 'What is the one main point you want these dads to walk away ready to implement?',
    emptyLabel: 'No main takeaway submitted yet.',
  },
  {
    key: 'foundational_scripture',
    label: 'What is one Scripture that could serve as a foundation for that point or lesson?',
    emptyLabel: 'No Scripture submitted yet.',
  },
];

const formatGuideQueueDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatFocusAreaLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatCampaignDateTime = (value) => {
  if (!value) return 'Pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Pending';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getHistoryStatusClasses = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'sent') {
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200';
  }
  if (normalized === 'failed') {
    return 'border-rose-500/30 bg-rose-500/10 text-rose-200';
  }
  if (normalized === 'skipped') {
    return 'border-amber-500/30 bg-amber-500/10 text-amber-200';
  }
  return 'border-charcoal-lighter bg-charcoal text-gray-300';
};

const getGuideSchedulingNextMove = (guide = {}) => {
  const status = String(guide.status || '').trim().toLowerCase();
  const inviteStage = String(guide.invite_stage || '').trim().toLowerCase();

  if (status === 'scheduled' || inviteStage === 'scheduled') {
    return 'Confirm prep and story focus';
  }
  if (inviteStage === 'onboarding_sent') {
    return 'Check onboarding progress';
  }
  return 'Schedule conversation';
};

const GuestGuidesTab = ({
  adminFetch,
  authLoading = false,
  hasAdminToken = false,
  activeCampaigns = [],
  deliveryIssues = [],
  guideSummary = null,
  refreshGuideSummary = null,
  workflowKey = null,
  workflowRequestKey = null,
}) => {
  const guidePartnersState = useGuidePartnersAdminState({
    adminFetch,
    authLoading,
    hasAdminToken,
    activeCampaigns,
    deliveryIssues,
    guideSummary,
    refreshGuideSummary,
    workflowKey,
    workflowRequestKey,
  });
  const {
    guides,
    statusCounts,
    loading,
    viewMode,
    setViewMode,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    searchQuery,
    setSearchQuery,
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    showEmailModal,
    setShowEmailModal,
    showScheduleModal,
    setShowScheduleModal,
    showImportModal,
    setShowImportModal,
    showSequenceModal,
    showReactivationModal,
    showManualContactModal,
    selectedGuide,
    setSelectedGuide,
    selectedGuideIds,
    availableDocuments,
    sequences,
    campaignPreview,
    suppressedGuideDeliveryById,
    formData,
    setFormData,
    emailData,
    setEmailData,
    scheduleData,
    setScheduleData,
    importFileName,
    parsedImportGuides,
    importParseError,
    saving,
    uploadingGuidePhotoId,
    analyzing,
    loadingSequences,
    previewingCampaign,
    campaignSubmitting,
    campaignForm,
    reactivationForm,
    manualContactForm,
    manualContactSubmitting,
    manualContactSelectedGuides,
    outreachSectionRef,
    selectedSequence,
    invitedReactivationSequence,
    selectedSequenceSteps,
    selectedStep,
    remainingSequenceSteps,
    activeCampaignGuideIds,
    guideAttention,
    readyToScheduleGuides,
    allReadyToScheduleGuides,
    hasMoreReadyToSchedule,
    showAllReadyToSchedule,
    toggleReadyToScheduleExpansion,
    guideSections,
    expandedGuideSections,
    toggleGuideSection,
    staleInvitedGuides,
    selectedCount,
    sequenceRecipientCount,
    sequenceSkippedCount,
    resetImportState,
    clearSelection,
    toggleGroupSelectionByInviteStage,
    openSequenceModal,
    openReactivationModal,
    closeSequenceModal,
    closeReactivationModal,
    openManualContactModal,
    closeManualContactModal,
    handleManualContactFormChange,
    handleLogManualContact,
    handleCampaignFormChange,
    handleReactivationFormChange,
    handleChainDelayChange,
    handleCreateCampaign,
    handleCreateReactivationCampaign,
    handleAddGuide,
    handleUpdateGuide,
    handleDeleteGuide,
    handleSendEmail,
    handleSchedule,
    handleImport,
    handleRunAnalysis,
    handleMarkComplete,
    handleReactivate,
    openEditModal,
    handleGuidePhotoUpload,
    openEmailModal,
    handleImportFileChange,
    openScheduleModal,
    toggleFocusArea,
    toggleDocument,
    toggleReactivationGuide,
    handleOpenAddModal,
    toggleGuideSelection,
    guestGuideAbortRef,
  } = guidePartnersState;

  const selectedGuideOutreachHistory = Array.isArray(selectedGuide?.outreach_history)
    ? [...selectedGuide.outreach_history]
        .filter(Boolean)
        .sort((a, b) => new Date(b?.sent_at || 0).getTime() - new Date(a?.sent_at || 0).getTime())
    : [];
  const selectedGuideEmailHistory = Array.isArray(selectedGuide?.email_history)
    ? [...selectedGuide.email_history]
        .filter((item) => item && item.kind !== 'sequence')
        .sort((a, b) => new Date(b?.sent_at || 0).getTime() - new Date(a?.sent_at || 0).getTime())
    : [];
  const reactivationSelectedGuides = staleInvitedGuides.filter((guide) =>
    reactivationForm.guide_ids.includes(guide.guide_id)
  );
  // --- Send guards ------------------------------------------------------
  // Both outreach buttons below can start a real send, and neither can be
  // stopped once it starts: there is no pause, cancel or delete endpoint for a
  // campaign. So each one now requires a second, deliberate confirmation shown
  // against the full recipient roster by name.
  const [reactivationArmed, setReactivationArmed] = React.useState(false);
  const [campaignArmed, setCampaignArmed] = React.useState(false);

  const campaignRecipients = campaignPreview?.recipients || [];
  const reactivationRecipientKey = reactivationSelectedGuides.map((guide) => guide.guide_id).join('|');
  const campaignRecipientKey = campaignRecipients.map((recipient) => recipient.guide_id).join('|');

  // Any change to who is on the list, or to how it would send, disarms the
  // confirmation so a stale roster can never be the thing you approved.
  React.useEffect(() => {
    setReactivationArmed(false);
  }, [reactivationRecipientKey]);

  React.useEffect(() => {
    setCampaignArmed(false);
  }, [
    campaignRecipientKey,
    campaignForm.delivery_mode,
    campaignForm.sequence_id,
    campaignForm.step_key,
    campaignForm.auto_chain_sequence,
  ]);

  const closeReactivationModalGuarded = () => {
    setReactivationArmed(false);
    closeReactivationModal();
  };

  const closeSequenceModalGuarded = () => {
    setCampaignArmed(false);
    closeSequenceModal();
  };

  const campaignSendRateSummary = () => {
    const count = campaignRecipients.length;
    if (campaignForm.delivery_mode !== 'staggered') {
      return `All ${count} email${count === 1 ? '' : 's'} send immediately, in a single batch.`;
    }
    const size = Number(campaignForm.batch_size) || count;
    const every = Number(campaignForm.cadence_interval) || 1;
    const unit = campaignForm.cadence_type === 'hourly_interval' ? 'hour' : 'day';
    const when = campaignForm.start_date && campaignForm.start_time
      ? `${campaignForm.start_date} at ${campaignForm.start_time}`
      : 'the start time you set above';
    return `${count} recipient${count === 1 ? '' : 's'}, ${size} at a time, every ${every} ${unit}${every === 1 ? '' : 's'}, starting ${when}. If that time is now or already past, the first batch sends immediately.`;
  };

  const renderSendConfirmation = (heading, rateLine, people, extraWarning = null) => (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
        <h4 className="text-sm font-semibold text-amber-200">{heading}</h4>
        <p className="mt-2 text-sm text-amber-100/90">{rateLine}</p>
        <p className="mt-2 text-sm text-amber-100/90">
          There is no cancel. Once a send starts, nothing in this app stops the rest of it.
        </p>
        {extraWarning}
      </div>
      <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
        <h4 className="text-sm font-semibold text-white">
          {people.length} {people.length === 1 ? 'person' : 'people'} will be emailed
        </h4>
        <div className="mt-3 max-h-80 space-y-1 overflow-auto">
          {people.map((person, index) => (
            <div
              key={person.guide_id || `${person.name || 'guide'}-${index}`}
              className="flex items-center justify-between gap-3 rounded-md bg-charcoal-light px-3 py-2 text-sm"
            >
              <span className="truncate text-white">{person.name || 'Unnamed Guide Partner'}</span>
              <span className="truncate text-xs text-foreground/70">{person.email || 'No email address saved'}</span>
            </div>
          ))}
          {people.length === 0 && (
            <p className="text-sm text-gray-400">No recipients resolved. Nothing would send.</p>
          )}
        </div>
      </div>
    </div>
  );

  const activeGuideCampaigns = Array.isArray(guideAttention?.activeCampaigns)
    ? guideAttention.activeCampaigns
    : [];
  const nextMoveSummaryItems = [
    guideAttention.counts.onboardingCompleted > 0
      ? {
          key: 'ready_to_schedule',
          toneClasses: 'border-teal-500/30 bg-teal-500/10 text-teal-200',
          title: `${guideAttention.counts.onboardingCompleted} ready to schedule`,
          detail: 'Use Scheduling Context below as the work board for locking dates and comparing guide stories side by side.',
        }
      : null,
    staleInvitedGuides.length > 0
      ? {
          key: 'stale_invited',
          toneClasses: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
          title: `${staleInvitedGuides.length} invited guide${staleInvitedGuides.length === 1 ? '' : 's'} need follow-up`,
          detail: 'Guide Partner Outreach is the next place to reactivate older invited guides who have gone quiet.',
        }
      : null,
    guideAttention.counts.onboardingSentOverdue > 0
      ? {
          key: 'overdue_onboarding',
          toneClasses: 'border-orange-500/30 bg-orange-500/10 text-orange-200',
          title: `${guideAttention.counts.onboardingSentOverdue} overdue on onboarding`,
          detail: 'Review who is past the normal reminder window and decide where a personal nudge is needed.',
        }
      : null,
  ].filter(Boolean).slice(0, 3);

  const getStatusBadge = (status) => {
    const colors = {
      prospect: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-400',
      invited: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-400',
      active: 'border-green-200 bg-green-50 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-400',
      scheduled: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-500/30 dark:bg-purple-500/10 dark:text-purple-400',
      completed: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-gray-500/30 dark:bg-gray-500/10 dark:text-gray-400'
    };
    return <Badge className={colors[status] || colors.prospect}>{status}</Badge>;
  };

  const getInviteStageBadge = (inviteStage) => {
    const stage = inviteStage || 'not_invited';
    const colors = {
      not_invited: 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-300',
      invited: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
      interested: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
      onboarding_sent: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300',
      onboarding_completed: 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300',
      scheduled: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300',
      completed: 'border-zinc-200 bg-zinc-100 text-zinc-700 dark:border-zinc-500/30 dark:bg-zinc-500/10 dark:text-zinc-300',
      declined: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300',
      backburner: 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300',
    };
    const label = INVITE_STAGE_OPTIONS.find((option) => option.value === stage)?.label || stage;
    return <Badge className={colors[stage] || colors.not_invited}>{label}</Badge>;
  };

  // Modal Component
  // Modal Component - defined outside to prevent re-renders
  const renderModal = (show, onClose, title, children, onSubmit, submitText = 'Save', submitting = false) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-charcoal-light border border-charcoal-lighter rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-charcoal-lighter">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">{children}</div>
          <div className="flex justify-end gap-3 p-4 border-t border-charcoal-lighter">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={onSubmit} disabled={submitting} className="bg-aqua hover:bg-aqua-dark text-charcoal">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitText}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Guide Form JSX - inlined to prevent focus loss
  const guideFormJSX = (
    <GuestGuideForm
      formData={formData}
      setFormData={setFormData}
      selectedGuide={selectedGuide}
      uploadingGuidePhotoId={uploadingGuidePhotoId}
      handleGuidePhotoUpload={handleGuidePhotoUpload}
      toggleFocusArea={toggleFocusArea}
    />
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Mic className="w-5 h-5 text-aqua" />
            Guide Partners
          </h2>
          <p className="text-sm text-gray-400">Manage presenters and guest speakers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportModal(true)} className="text-gray-300">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button onClick={handleOpenAddModal} className="bg-aqua hover:bg-aqua-dark text-charcoal">
            <Plus className="w-4 h-4 mr-2" />
            Add Guide
          </Button>
        </div>
      </div>

      <Card className="bg-charcoal-light border-charcoal-lighter p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Kevin Next Moves</h3>
            <p className="mt-1 max-w-3xl text-sm text-gray-400">
              Start here when the next step needs your judgment. This queue mixes scheduling-ready Guide Partners with overdue onboarding follow-up so the personal work is visible before the full list.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 font-medium text-teal-200">
              {guideAttention.counts.onboardingCompleted} ready to schedule
            </div>
            <div className="rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 font-medium text-orange-200">
              {guideAttention.counts.onboardingSentOverdue} overdue onboarding
            </div>
            <div className="rounded-full bg-charcoal px-3 py-1 text-gray-300">
              {nextMoveSummaryItems.length} priorities
            </div>
          </div>
        </div>

        {nextMoveSummaryItems.length > 0 ? (
          <div className="mt-5 grid gap-4 xl:grid-cols-3">
            {nextMoveSummaryItems.map((item, index) => (
              <div key={item.key} className="rounded-2xl border border-charcoal-lighter bg-charcoal p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-charcoal-light text-xs font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className={`rounded-full border px-3 py-1 text-sm font-medium ${item.toneClasses}`}>
                    Next move
                  </span>
                </div>
                <p className="mt-4 text-lg font-semibold text-white">{item.title}</p>
                <p className="mt-2 text-sm leading-6 text-gray-300">{item.detail}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-dashed border-charcoal-lighter bg-charcoal px-4 py-6 text-sm text-gray-400">
            No immediate personal queue is active right now. Use the sequence tools for shared outreach or the full list below when you need to review the wider pipeline.
          </div>
        )}
      </Card>

      {allReadyToScheduleGuides.length > 0 && (
        <Card className="bg-charcoal-light border-charcoal-lighter p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-300/80">
                Scheduling Context
              </p>
              <h3 className="mt-2 text-lg font-semibold text-white">
                Compare the Guide Partners who are ready to schedule
              </h3>
              <p className="mt-2 max-w-3xl text-sm text-gray-400">
                Read their story, main takeaway, and Scripture side by side before you lock in a date. This stays focused on the people who already completed onboarding.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-sm font-medium text-teal-200">
                {allReadyToScheduleGuides.length} ready now
              </div>
              {hasMoreReadyToSchedule ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleReadyToScheduleExpansion}
                  className="border-charcoal-lighter text-foreground"
                >
                  {showAllReadyToSchedule
                    ? 'Show fewer'
                    : `Show all ${allReadyToScheduleGuides.length} ready to schedule`}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <div className="min-w-[840px] overflow-hidden rounded-2xl border border-charcoal-lighter bg-charcoal">
              <div className="grid min-w-full" style={{ gridTemplateColumns: `200px repeat(${readyToScheduleGuides.length}, minmax(220px, 1fr))` }}>
                <div className="border-b border-r border-charcoal-lighter px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Onboarding prompts</p>
                  <p className="mt-2 text-sm text-gray-400">
                    Use this matrix to compare what each Guide Partner is actually bringing before you schedule them.
                  </p>
                </div>
                {readyToScheduleGuides.map((guide) => (
                  <div key={guide.guide_id} className="border-b border-charcoal-lighter px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-semibold text-white">{guide.name || 'Unnamed Guide Partner'}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-400">
                          {guide.phone ? <p>{guide.phone}</p> : null}
                          {guide.email ? <p className="min-w-0 break-all">{guide.email}</p> : null}
                        </div>
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                          Next move
                        </p>
                        <p className="mt-1 text-sm text-gray-200">
                          {getGuideSchedulingNextMove(guide)}
                        </p>
                        <div className="mt-3 flex items-center gap-2 whitespace-nowrap">
                          {guide.phone ? (
                            <>
                              <Button asChild size="sm" variant="outline" className="h-8 gap-1 border-charcoal-lighter px-2 text-foreground">
                                <a href={`tel:${guide.phone}`}>
                                  <Phone className="h-3.5 w-3.5" />
                                  Call
                                </a>
                              </Button>
                              <Button asChild size="sm" variant="outline" className="h-8 gap-1 border-charcoal-lighter px-2 text-foreground">
                                <a href={`sms:${guide.phone}`}>
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  Text
                                </a>
                              </Button>
                            </>
                          ) : null}
                          {guide.email ? (
                            <Button asChild size="sm" variant="outline" className="h-8 gap-1 border-charcoal-lighter px-2 text-foreground">
                              <a href={`mailto:${guide.email}`}>
                                <Mail className="h-3.5 w-3.5" />
                                Email
                              </a>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          onClick={() => openScheduleModal(guide)}
                          size="icon"
                          className="h-9 w-9 bg-aqua text-charcoal hover:bg-aqua-dark"
                          title="Schedule"
                          aria-label={`Schedule ${guide.name || 'guide partner'}`}
                        >
                          <Calendar className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => openEditModal(guide)}
                          size="icon"
                          className="h-9 w-9 border-charcoal-lighter text-foreground"
                          title="Edit"
                          aria-label={`Edit ${guide.name || 'guide partner'}`}
                        >
                          <PencilLine className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {guide.primary_focus_area ? (
                        <span className="rounded-full border border-teal-500/30 bg-teal-500/10 px-2.5 py-1 text-xs font-medium text-teal-200">
                          {formatFocusAreaLabel(guide.primary_focus_area)}
                        </span>
                      ) : null}
                      {formatGuideQueueDate(guide.onboarding_completed_at) ? (
                        <span className="rounded-full border border-charcoal-lighter bg-charcoal-light px-2.5 py-1 text-xs font-medium text-gray-300">
                          Completed {formatGuideQueueDate(guide.onboarding_completed_at)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}

                {SCHEDULING_CONTEXT_ROWS.map((row, rowIndex) => (
                  <React.Fragment key={row.key}>
                    <div className={`border-r border-charcoal-lighter px-4 py-4 ${rowIndex < SCHEDULING_CONTEXT_ROWS.length - 1 ? 'border-b' : ''} border-charcoal-lighter`}>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                        {rowIndex + 1}
                      </p>
                      <p className="mt-2 text-sm font-medium leading-6 text-gray-200">
                        {row.label}
                      </p>
                    </div>
                    {readyToScheduleGuides.map((guide) => {
                      const value = String(guide?.[row.key] || '').trim();
                      return (
                        <div
                          key={`${guide.guide_id}-${row.key}`}
                          className={`px-4 py-4 ${rowIndex < SCHEDULING_CONTEXT_ROWS.length - 1 ? 'border-b' : ''} border-charcoal-lighter`}
                        >
                          <p className={`text-sm leading-6 ${value ? 'text-gray-200' : 'italic text-gray-500'}`}>
                            {value || row.emptyLabel}
                          </p>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map(opt => (
          <button key={opt.value} onClick={() => setStatusFilter(opt.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              statusFilter === opt.value
                ? 'bg-aqua text-charcoal'
                : 'bg-charcoal-light text-gray-400 hover:bg-charcoal-lighter'
            }`}>
            {opt.label}
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              statusFilter === opt.value ? 'bg-charcoal/20' : 'bg-charcoal'
            }`}>
              {statusCounts[opt.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search, Sort, View Controls */}
      <Card className="bg-charcoal-light border-charcoal-lighter p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-charcoal border-charcoal-lighter text-white w-full"
              placeholder="Search by name, email, or bio..." />
          </div>
          <div className="flex items-center gap-2">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-md bg-charcoal border border-charcoal-lighter text-white text-sm">
              <option value="created_at">Date Added</option>
              <option value="name">Name</option>
              <option value="status">Status</option>
              <option value="scheduled_date">Scheduled Date</option>
            </select>
            <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 bg-charcoal border border-charcoal-lighter rounded-md text-gray-400 hover:text-white">
              {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <div className="flex border border-charcoal-lighter rounded-md overflow-hidden">
              <button onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-aqua text-charcoal' : 'bg-charcoal text-gray-400'}`}>
                <Grid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-aqua text-charcoal' : 'bg-charcoal text-gray-400'}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card ref={outreachSectionRef} className="scroll-mt-40 bg-charcoal-light border-charcoal-lighter p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white">Guide Partner Outreach</h3>
            <p className="text-xs text-gray-400">
              Use the shared sequence when the next touch is the same. If someone is ready to schedule or needs a more personal note, handle that directly instead of forcing it through automation.
            </p>
            {staleInvitedGuides.length > 0 && (
              <p className="mt-2 text-xs text-amber-300">
                {staleInvitedGuides.length} invited Guide Partner{staleInvitedGuides.length === 1 ? ' is' : 's are'} older than 30 days. Consider sending a reactivation email.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => toggleGroupSelectionByInviteStage('not_invited')}
              className="text-foreground"
            >
              Select All Not Invited
            </Button>
            <Button
              variant="outline"
              onClick={() => toggleGroupSelectionByInviteStage('invited')}
              className="text-foreground"
            >
              Select All Invited
            </Button>
            <Button variant="outline" onClick={clearSelection} className="text-foreground">
              Clear Selection
            </Button>
            <Button
              variant="outline"
              onClick={openManualContactModal}
              disabled={selectedCount === 0}
              className="text-foreground"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Mark Contacted by Hand
            </Button>
            <Button
              variant="outline"
              onClick={openReactivationModal}
              disabled={staleInvitedGuides.length === 0 || !invitedReactivationSequence}
              className="text-foreground"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Reactivate Invited
            </Button>
            <Button
              onClick={openSequenceModal}
              disabled={selectedCount === 0 || loadingSequences}
              className="bg-aqua hover:bg-aqua-dark text-charcoal"
            >
              {loadingSequences ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Send Guide Partner Email Series
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <div className={`rounded-full border px-3 py-1 ${
            guideAttention.counts.onboardingCompleted > 0
              ? 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300'
              : 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-300'
          }`}>
            {guideAttention.counts.onboardingCompleted} ready to schedule
          </div>
          <div className={`rounded-full border px-3 py-1 ${
            guideAttention.counts.onboardingSentOverdue > 0
              ? 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300'
              : 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-300'
          }`}>
            {guideAttention.counts.onboardingSentOverdue} overdue onboarding
          </div>
          <div className="rounded-full bg-charcoal px-3 py-1 text-white">
            {selectedCount} selected
          </div>
          <div className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            {activeCampaignGuideIds.size} already in queued sequence
          </div>
          <div className="rounded-full bg-charcoal px-3 py-1 text-foreground/75">
            {guides.filter((guide) => (guide.invite_stage || 'not_invited') === 'not_invited').length} not invited
          </div>
          <div className="rounded-full bg-charcoal px-3 py-1 text-foreground/75">
            {guides.filter((guide) => (guide.invite_stage || 'not_invited') === 'invited').length} invited
          </div>
          <div className={`rounded-full border px-3 py-1 ${
            staleInvitedGuides.length > 0
              ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300'
              : 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-300'
          }`}>
            {staleInvitedGuides.length} stale invited
          </div>
          <div className="rounded-full bg-charcoal px-3 py-1 text-foreground/75">
            {guides.filter((guide) => (guide.invite_stage || 'not_invited') === 'onboarding_sent').length} onboarding sent
          </div>
        </div>
        {activeGuideCampaigns.length > 0 && (
          <div className="mt-4 rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-white">Campaigns in progress</h4>
                <p className="text-xs text-gray-400">
                  These Guide Partner campaigns are still active or queued.
                </p>
              </div>
              <div className="rounded-full bg-charcoal-light px-3 py-1 text-xs text-gray-300">
                {activeGuideCampaigns.length} active
              </div>
            </div>
            <div className="mt-3 space-y-3">
              {activeGuideCampaigns.slice(0, 3).map((campaign) => (
                <div
                  key={campaign.campaign_id}
                  className="rounded-lg border border-charcoal-lighter bg-charcoal-light p-3"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="border-aqua/30 bg-aqua/10 text-aqua">
                          {campaign.step_name_snapshot || 'Campaign'}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {campaign.sequence_name_snapshot || 'Guide Partner campaign'}
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-white">
                        {campaign.subject_snapshot || 'No subject saved'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>{campaign.sent_count || 0} sent</span>
                        <span>{campaign.remaining_count || 0} remaining</span>
                        <span>{campaign.failed_count || 0} failed</span>
                        <span>{campaign.delivery_skipped_count || 0} skipped</span>
                      </div>
                    </div>
                    <div className="shrink-0 rounded-lg border border-charcoal-lighter bg-charcoal px-3 py-2 text-xs text-gray-300">
                      <p>Status: {campaign.status || 'unknown'}</p>
                      <p>Next batch: {formatCampaignDateTime(campaign.next_run_at)}</p>
                      <p>
                        Batch size: {campaign.batch_size || campaign.recipient_count || 0}
                        {campaign.cadence_type === 'hourly_interval'
                          ? `, every ${campaign.cadence_interval || 1} hour`
                          : campaign.cadence_type === 'daily'
                            ? `, every ${campaign.cadence_interval || 1} day`
                            : ''}
                        {campaign.cadence_interval > 1 && campaign.cadence_type === 'hourly_interval' ? 's' : ''}
                        {campaign.cadence_interval > 1 && campaign.cadence_type === 'daily' ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Guides Display */}
      <GuestGuidesList
        loading={loading}
        guides={guides}
        viewMode={viewMode}
        getStatusBadge={getStatusBadge}
        getInviteStageBadge={getInviteStageBadge}
        onAddGuide={handleOpenAddModal}
        onOpenEditModal={openEditModal}
        onOpenEmailModal={openEmailModal}
        onOpenScheduleModal={openScheduleModal}
        onDeleteGuide={handleDeleteGuide}
        onMarkComplete={handleMarkComplete}
        onReactivate={handleReactivate}
        selectedGuideIds={selectedGuideIds}
        onToggleGuideSelection={toggleGuideSelection}
        activeCampaignGuideIds={activeCampaignGuideIds}
        suppressedGuideDeliveryById={suppressedGuideDeliveryById}
        guideSections={guideSections}
        expandedGuideSections={expandedGuideSections}
        onToggleGuideSection={toggleGuideSection}
      />

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-aqua" />
            AI Guide Partners Analysis
          </h3>
          <Button
            onClick={handleRunAnalysis}
            disabled={analyzing}
            className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        </div>
        <p className="text-gray-500 text-center py-8">
          Click &quot;Run Analysis&quot; to get AI-powered insights from Guide Partner profiles, talks, and scheduling.
        </p>
      </Card>

      {/* Add Modal */}
      {renderModal(showAddModal, () => { guestGuideAbortRef.current?.abort(); setShowAddModal(false); }, "Add Guide Partner",
        guideFormJSX,
        handleAddGuide, "Add Guide", saving)}

      {/* Edit Modal */}
      {renderModal(showEditModal, () => { guestGuideAbortRef.current?.abort(); setShowEditModal(false); setSelectedGuide(null); }, "Edit Guide Partner",
        <div className="space-y-6">
          {guideFormJSX}
          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-white">Guide Partner outreach history</h4>
                <p className="mt-1 text-xs leading-5 text-gray-400">
                  Use this as the durable record of which shared sequence steps or one-off emails this person already received.
                </p>
              </div>
              {selectedGuide?.last_outreach_sent_at ? (
                <div className="rounded-full border border-charcoal-lighter bg-charcoal-light px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-gray-300">
                  Last touch {selectedGuide?.last_outreach_step_key ? String(selectedGuide.last_outreach_step_key).replace('_', ' ') : 'recorded'} on {formatDate(selectedGuide.last_outreach_sent_at)}
                </div>
              ) : null}
            </div>

            <div className="mt-4 space-y-3">
              {selectedGuideOutreachHistory.length === 0 ? (
                <p className="text-sm text-gray-400">No shared-sequence history recorded yet.</p>
              ) : (
                selectedGuideOutreachHistory.slice(0, 8).map((item, index) => (
                  <div key={`${item?.sent_at || 'outreach'}-${item?.step_key || index}`} className="rounded-lg border border-charcoal-lighter bg-charcoal-light p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.16em] ${getHistoryStatusClasses(item?.status)}`}>
                        {item?.status || 'recorded'}
                      </span>
                      <span className="text-sm font-medium text-white">
                        {item?.step_name || item?.step_key || 'Sequence step'}
                      </span>
                      {item?.sequence_name ? (
                        <span className="text-xs text-gray-400">{item.sequence_name}</span>
                      ) : null}
                    </div>
                    {item?.subject ? (
                      <p className="mt-2 text-sm text-gray-200">{item.subject}</p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                      <span>{formatDate(item?.sent_at)}</span>
                      {item?.reason ? <span>Reason: {item.reason}</span> : null}
                      {item?.error ? <span>Error: {item.error}</span> : null}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6">
              <h5 className="text-sm font-semibold text-white">Direct and system emails</h5>
              <div className="mt-3 space-y-3">
                {selectedGuideEmailHistory.length === 0 ? (
                  <p className="text-sm text-gray-400">No direct or reminder email history recorded yet.</p>
                ) : (
                  selectedGuideEmailHistory.slice(0, 8).map((item, index) => (
                    <div key={`${item?.sent_at || 'email'}-${item?.subject || index}`} className="rounded-lg border border-charcoal-lighter bg-charcoal-light p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.16em] ${getHistoryStatusClasses(item?.status)}`}>
                          {item?.status || 'recorded'}
                        </span>
                        <span className="text-sm font-medium text-white">
                          {item?.type ? String(item.type).replace(/_/g, ' ') : 'Direct email'}
                        </span>
                      </div>
                      {item?.subject ? (
                        <p className="mt-2 text-sm text-gray-200">{item.subject}</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>{formatDate(item?.sent_at)}</span>
                        {item?.error ? <span>Error: {item.error}</span> : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>,
        handleUpdateGuide, "Save Changes", saving)}

      {/* Email Modal */}
      {renderModal(showEmailModal, () => { setShowEmailModal(false); setSelectedGuide(null); }, `Email ${selectedGuide?.name || ''}`,
        <div className="space-y-4">
          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4 text-sm text-gray-300">
            Use a one-off note when this person needs a warmer or more specific follow-up than the shared sequence can provide. If they already completed onboarding, keep the message short and move the calendar handoff forward.
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">To</label>
            <Input value={selectedGuide?.email || ''} disabled className="bg-charcoal border-charcoal-lighter text-gray-400" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Subject *</label>
            <Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              className="bg-charcoal border-charcoal-lighter text-white" placeholder="Invitation to speak at Dad Partners" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Message *</label>
            <Textarea value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
              className="bg-charcoal border-charcoal-lighter text-white min-h-[150px]"
              placeholder="Hi [Name],&#10;&#10;We'd love to have you share your story..." />
          </div>
          <div>
            <label className="text-sm text-gray-400 dark:text-gray-400 mb-2 block">Attach Documents</label>
            <div className="space-y-2">
              {availableDocuments.map(doc => (
                <label key={doc.id} className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-charcoal rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-charcoal-lighter border border-gray-200 dark:border-charcoal-lighter">
                  <input type="checkbox" checked={emailData.documents.includes(doc.id)}
                    onChange={() => toggleDocument(doc.id)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-charcoal-lighter accent-aqua" />
                  <div>
                    <p className="text-gray-800 dark:text-white text-sm">{doc.name}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{doc.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>,
        handleSendEmail, "Send Email", saving)}

      {/* Schedule Modal */}
      {renderModal(showScheduleModal, () => { setShowScheduleModal(false); setSelectedGuide(null); }, `Schedule ${selectedGuide?.name || ''}`,
        <div className="space-y-4">
          {selectedGuide && (
            <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-white">Scheduling context</p>
                  <p className="mt-1 text-sm text-gray-400">
                    Keep their submitted story and takeaway in view while you choose the date and topic.
                  </p>
                </div>
                {selectedGuide?.primary_focus_area ? (
                  <span className="rounded-full border border-teal-500/30 bg-teal-500/10 px-2.5 py-1 text-xs font-medium text-teal-200">
                    {formatFocusAreaLabel(selectedGuide.primary_focus_area)}
                  </span>
                ) : null}
              </div>
              <div className="mt-4 space-y-3">
                {SCHEDULING_CONTEXT_ROWS.map((row) => {
                  const value = String(selectedGuide?.[row.key] || '').trim();
                  if (!value) return null;

                  return (
                    <div key={row.key}>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                        {row.label}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-200">
                        {value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Presentation Date *</label>
            <Input type="date" value={scheduleData.scheduled_date} onChange={(e) => setScheduleData({ ...scheduleData, scheduled_date: e.target.value })}
              className="bg-charcoal border-charcoal-lighter text-white" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Topic</label>
            <Input value={scheduleData.scheduled_topic} onChange={(e) => setScheduleData({ ...scheduleData, scheduled_topic: e.target.value })}
              className="bg-charcoal border-charcoal-lighter text-white" placeholder="e.g., Navigating Career Transitions" />
          </div>
        </div>,
        handleSchedule, "Schedule Presentation", saving)}

      {/* Sequence Modal */}
      {renderModal(showSequenceModal, closeSequenceModalGuarded, 'Send Guide Partner Email Series',
        campaignArmed ? renderSendConfirmation(
          'Confirm this Guide Partner send',
          campaignSendRateSummary(),
          campaignRecipients,
          campaignForm.auto_chain_sequence ? (
            <p className="mt-2 text-sm text-amber-100/90">
              Auto-chain is on. Confirming also arms the remaining steps of this sequence as separate campaigns, with the copy frozen as it reads today. Rewriting the sequence later will not change them, and they cannot be cancelled.
            </p>
          ) : null
        ) : (
        <div className="space-y-5">
          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded-full bg-aqua/15 px-3 py-1 text-aqua">{selectedCount} selected</span>
              <span className="rounded-full bg-charcoal-light px-3 py-1 text-gray-300">
                {sequenceRecipientCount} with email
              </span>
              <span className="rounded-full bg-charcoal-light px-3 py-1 text-gray-300">
                {sequenceSkippedCount} skipped
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-300">
              Use this for a shared invite or reminder step only when the same next move applies to the whole group. Pull out anyone who now needs a personal note or a scheduling handoff.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Sequence</label>
            <select
              value={campaignForm.sequence_id}
              onChange={(e) => handleCampaignFormChange('sequence_id', e.target.value)}
              className="w-full rounded-md border border-charcoal-lighter bg-charcoal px-3 py-2 text-white"
            >
              <option value="">Choose a sequence</option>
              {sequences.map((sequence) => (
                <option key={sequence.sequence_id} value={sequence.sequence_id}>
                  {sequence.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Email In Sequence</label>
            <select
              value={campaignForm.step_key}
              onChange={(e) => handleCampaignFormChange('step_key', e.target.value)}
              className="w-full rounded-md border border-charcoal-lighter bg-charcoal px-3 py-2 text-white"
              disabled={!selectedSequence}
            >
              <option value="">Choose an email</option>
              {selectedSequenceSteps.map((step) => (
                <option key={step.step_key} value={step.step_key}>
                  {step.step_name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Subject</label>
              <Input
                value={campaignPreview?.subject || selectedStep?.subject || ''}
                readOnly
                className="bg-charcoal border-charcoal-lighter text-foreground"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Delivery</label>
              <select
                value={campaignForm.delivery_mode}
                onChange={(e) => handleCampaignFormChange('delivery_mode', e.target.value)}
                className="w-full rounded-md border border-charcoal-lighter bg-charcoal px-3 py-2 text-white"
              >
                <option value="send_now">Send Now</option>
                <option value="staggered">Stagger Delivery</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Preview</label>
            <Textarea
              value={campaignPreview?.body || selectedStep?.body || ''}
              readOnly
              className="min-h-[220px] bg-charcoal border-charcoal-lighter text-foreground"
            />
          </div>

          {remainingSequenceSteps.length > 0 && (
            <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4 space-y-4">
              <div className="flex items-start gap-3">
                <input
                  id="auto-chain-sequence"
                  type="checkbox"
                  checked={campaignForm.auto_chain_sequence}
                  onChange={(e) => handleCampaignFormChange('auto_chain_sequence', e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-charcoal-lighter bg-charcoal text-aqua focus:ring-aqua"
                />
                <div>
                  <label htmlFor="auto-chain-sequence" className="text-sm font-semibold text-white">
                    Continue the rest of this sequence automatically
                  </label>
                  <p className="mt-1 text-xs text-gray-400">
                    After {selectedStep?.step_name || 'this email'} goes out, Dad Partners can automatically schedule the remaining emails in this sequence for the same selected guides.
                  </p>
                </div>
              </div>

              {campaignForm.auto_chain_sequence && (
                <div className="grid gap-4 md:grid-cols-2">
                  {remainingSequenceSteps.map((step) => (
                    <div key={step.step_key} className="space-y-2">
                      <label className="text-sm text-gray-400">
                        Send {step.step_name} after how many days
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={campaignForm.chain_delays?.[step.step_key] ?? ''}
                        onChange={(e) => handleChainDelayChange(step.step_key, e.target.value)}
                        className="bg-charcoal-light border-charcoal-lighter text-white"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {campaignForm.delivery_mode === 'staggered' && (
            <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Batch Size</label>
                  <Input
                    type="number"
                    min="1"
                    value={campaignForm.batch_size}
                    onChange={(e) => handleCampaignFormChange('batch_size', e.target.value)}
                    className="bg-charcoal-light border-charcoal-lighter text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Cadence</label>
                  <select
                    value={campaignForm.cadence_type}
                    onChange={(e) => handleCampaignFormChange('cadence_type', e.target.value)}
                    className="w-full rounded-md border border-charcoal-lighter bg-charcoal-light px-3 py-2 text-white"
                  >
                    <option value="daily">Daily</option>
                    <option value="hourly_interval">Every X Hours</option>
                  </select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">
                    {campaignForm.cadence_type === 'daily' ? 'Every how many days' : 'Every how many hours'}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={campaignForm.cadence_interval}
                    onChange={(e) => handleCampaignFormChange('cadence_interval', e.target.value)}
                    className="bg-charcoal-light border-charcoal-lighter text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Start Date</label>
                  <Input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => handleCampaignFormChange('start_date', e.target.value)}
                    className="bg-charcoal-light border-charcoal-lighter text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Start Time</label>
                  <Input
                    type="time"
                    value={campaignForm.start_time}
                    onChange={(e) => handleCampaignFormChange('start_time', e.target.value)}
                    className="bg-charcoal-light border-charcoal-lighter text-white"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-white">Recipient Preview</h4>
                <p className="text-xs text-gray-400">
                  Showing the first few recipients from the guides you selected.
                </p>
              </div>
              {previewingCampaign && <Loader2 className="h-4 w-4 animate-spin text-aqua" />}
            </div>
            <div className="mt-3 space-y-2">
              {(campaignPreview?.recipients || []).slice(0, 6).map((recipient) => (
                <div key={recipient.guide_id} className="flex items-center justify-between rounded-md bg-charcoal-light px-3 py-2 text-sm">
                  <span className="text-white">{recipient.name || 'Unnamed Guide Partner'}</span>
                  <span className="text-foreground/70">{recipient.email}</span>
                </div>
              ))}
              {!previewingCampaign && (campaignPreview?.recipients || []).length === 0 && (
                <p className="text-sm text-gray-400">No deliverable recipients yet for this selection.</p>
              )}
              {sequenceSkippedCount > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {sequenceSkippedCount} selected guide{sequenceSkippedCount === 1 ? '' : 's'} will be skipped because no email address is saved.
                </p>
              )}
            </div>
          </div>
        </div>
        ),
        campaignArmed ? handleCreateCampaign : () => setCampaignArmed(true),
        campaignArmed
          ? `Yes \u2014 ${campaignForm.delivery_mode === 'staggered' ? 'schedule' : 'send to'} ${campaignRecipients.length} ${campaignRecipients.length === 1 ? 'person' : 'people'}`
          : 'Review recipients',
        campaignSubmitting)}

      {renderModal(showReactivationModal, closeReactivationModalGuarded, 'Reactivate Invited Guide Partners',
        reactivationArmed ? renderSendConfirmation(
          'Confirm this reactivation send',
          `The first 10 emails send the moment you confirm, then 10 more every hour until all ${reactivationSelectedGuides.length} have gone out.`,
          reactivationSelectedGuides,
          <p className="mt-2 text-sm text-amber-100/90">
            These are invited Guide Partners who have been quiet for 30+ days. If you have written to any of them by hand recently, go back and remove them before confirming.
          </p>
        ) : (
        <div className="space-y-5">
          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded-full bg-aqua/15 px-3 py-1 text-aqua">
                {reactivationSelectedGuides.length} selected
              </span>
              <span className="rounded-full bg-charcoal-light px-3 py-1 text-gray-300">
                first 10 send immediately
              </span>
              <span className="rounded-full bg-charcoal-light px-3 py-1 text-gray-300">
                then 10 every hour
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-300">
              This is a separate follow-up for invited Guide Partners who have gone quiet. It starts with stale invited guides older than 30 days, and you can remove anyone before sending.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Subject</label>
            <Input
              value={reactivationForm.subject}
              onChange={(e) => handleReactivationFormChange('subject', e.target.value)}
              className="bg-charcoal border-charcoal-lighter text-white"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Body</label>
            <Textarea
              value={reactivationForm.body}
              onChange={(e) => handleReactivationFormChange('body', e.target.value)}
              className="min-h-[280px] bg-charcoal border-charcoal-lighter text-white"
            />
          </div>

          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-white">Recipients</h4>
                <p className="text-xs text-gray-400">
                  Deselect anyone you do not want included in this reactivation send.
                </p>
              </div>
              <div className="text-xs text-gray-400">
                {staleInvitedGuides.length} stale invited total
              </div>
            </div>
            <div className="mt-3 max-h-72 space-y-2 overflow-auto">
              {staleInvitedGuides.map((guide) => (
                <label
                  key={guide.guide_id}
                  className="flex cursor-pointer items-start justify-between gap-3 rounded-md bg-charcoal-light px-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <input
                      type="checkbox"
                      checked={reactivationForm.guide_ids.includes(guide.guide_id)}
                      onChange={() => toggleReactivationGuide(guide.guide_id)}
                      className="mt-1 h-4 w-4 rounded border-charcoal-lighter bg-charcoal text-aqua focus:ring-aqua"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-white">{guide.name || 'Unnamed Guide Partner'}</p>
                      <p className="truncate text-xs text-gray-400">{guide.email || 'No email address saved'}</p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-gray-400">
                    <p>{guide.last_outreach_sent_at ? `Last touch ${formatDate(guide.last_outreach_sent_at)}` : 'No outreach date'}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        ),
        reactivationArmed ? handleCreateReactivationCampaign : () => setReactivationArmed(true),
        reactivationArmed
          ? `Yes \u2014 send to ${reactivationSelectedGuides.length} ${reactivationSelectedGuides.length === 1 ? 'person' : 'people'} now`
          : 'Review recipients',
        campaignSubmitting)}

      {/* Mark Contacted by Hand — records a contact Kevin already made. Sends nothing. */}
      {renderModal(showManualContactModal, closeManualContactModal, 'Mark Contacted by Hand',
        <div className="space-y-5">
          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4 text-sm text-gray-300">
            This sends no email. It records that you contacted these people yourself on the date below, so they count as recently touched and stay out of the next stale-invited follow-up.
          </div>

          <div className="rounded-lg border border-charcoal-lighter bg-charcoal p-4">
            <h4 className="text-sm font-semibold text-white">
              {manualContactSelectedGuides.length} {manualContactSelectedGuides.length === 1 ? 'person' : 'people'} will be marked
            </h4>
            <div className="mt-3 max-h-72 space-y-1 overflow-auto">
              {manualContactSelectedGuides.map((guide, index) => (
                <div
                  key={guide.guide_id || `manual-contact-${index}`}
                  className="flex items-center justify-between gap-3 rounded-md bg-charcoal-light px-3 py-2 text-sm"
                >
                  <span className="truncate text-white">{guide.name || guide.guide_id || 'Unnamed Guide Partner'}</span>
                  <span className="truncate text-xs text-foreground/70">
                    {guide.last_outreach_sent_at ? `Last touch ${formatDate(guide.last_outreach_sent_at)}` : 'No outreach date'}
                  </span>
                </div>
              ))}
              {manualContactSelectedGuides.length === 0 && (
                <p className="text-sm text-gray-400">No Guide Partners are selected. Nothing would be marked.</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Date you contacted them *</label>
            <Input
              type="date"
              value={manualContactForm.contacted_on}
              max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)}
              onChange={(e) => handleManualContactFormChange('contacted_on', e.target.value)}
              className="bg-charcoal border-charcoal-lighter text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Note (optional)</label>
            <Input
              value={manualContactForm.note}
              onChange={(e) => handleManualContactFormChange('note', e.target.value)}
              className="bg-charcoal border-charcoal-lighter text-white"
              placeholder="Personal email from my Gmail"
            />
          </div>
        </div>,
        handleLogManualContact,
        `Mark ${manualContactSelectedGuides.length} as contacted`,
        manualContactSubmitting)}

      {/* Import Modal */}
      {renderModal(showImportModal, () => { setShowImportModal(false); resetImportState(); }, "Import Guide Partners",
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">
            Upload a CSV file with either <code>name</code> or <code>first_name</code> + <code>last_name</code>, plus <code>email</code>.
            Optional columns: <code>phone</code>, <code>bio</code>, <code>focus_areas</code>, <code>expertise</code>, <code>status</code>, <code>invite_stage</code>.
          </p>
          <p className="text-xs text-gray-500">
            Header variations like <code>First Name</code>, <code>Last Name</code>, <code>Phone Number</code>, <code>Focus Areas</code>, and <code>Invite Stage</code> also work.
          </p>
          <div className="rounded-lg border border-dashed border-charcoal-lighter bg-charcoal p-4">
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 text-center">
              <Upload className="w-5 h-5 text-aqua" />
              <span className="text-sm font-medium text-white">Choose CSV file</span>
              <span className="text-xs text-gray-500">{importFileName || 'No file selected yet'}</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleImportFileChange}
              />
            </label>
          </div>
          {importParseError && (
            <p className="text-sm text-red-400">{importParseError}</p>
          )}
          {parsedImportGuides.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">Preview</p>
                <p className="text-xs text-gray-400">{parsedImportGuides.length} guide(s) ready to import</p>
              </div>
              <div className="max-h-64 overflow-auto rounded-lg border border-charcoal-lighter">
                <table className="w-full text-left text-sm">
                  <thead className="bg-charcoal">
                    <tr className="text-gray-400">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Phone</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Invite Stage</th>
                      <th className="px-3 py-2">Focus Areas</th>
                      <th className="px-3 py-2">Expertise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-lighter bg-charcoal-light">
                    {parsedImportGuides.map((guide, index) => (
                      <tr key={`${guide.email || guide.name}-${index}`}>
                        <td className="px-3 py-2 text-white">{guide.name || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.email || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.phone || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.status || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.invite_stage || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.focus_areas || '-'}</td>
                        <td className="px-3 py-2 text-gray-300">{guide.expertise || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>,
        handleImport, "Import", saving)}
    </div>
  );
};

export default GuestGuidesTab;
